import usePagedCollection from '../../hooks/usePagedCollection';
import InfiniteScrollSentinel from '../common/InfiniteScrollSentinel';
import { useState, useEffect } from 'react';
import { Loader, Input, Button, Dimmer } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import SusuToggle from '../common/SusuToggle';
import ClosetCard from './ClosetCard';
import './ClosetPanelView.css';

import { useClosetPageQuery, useCreateClosetMutation, useDeleteClosetMutation, useUpdateClosetMutation } from '../../redux/apis/endpoints/closetApi';

const ClosetPanelView = ({
  onClosetSelect,
  panelPosition = 'right',
  // Props for external control (used when header is in ResizableLayout)
  onStateChange
}) => {
  const [createClosetModalOpen, setCreateClosetModalOpen] = useState(false);
  const [newClosetName, setNewClosetName] = useState('');
  const [newClosetIsPublic, setNewClosetIsPublic] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedClosets, setSelectedClosets] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editingCloset, setEditingCloset] = useState(null);
  const [editClosetName, setEditClosetName] = useState('');
  const [editClosetIsPublic, setEditClosetIsPublic] = useState(false);

  // RTK Query hooks
  const { items: closets, isLoading: closetsLoading, isFetching, isError, hasMore, loadMore, refetch, reload } = usePagedCollection(useClosetPageQuery);
  const [createCloset, { isLoading: creatingCloset }] = useCreateClosetMutation();
  const [deleteCloset, { isLoading: deletingCloset }] = useDeleteClosetMutation();
  const [updateCloset, { isLoading: updatingCloset }] = useUpdateClosetMutation();

  // Sort closets: default closet first, then most recently edited to oldest
  const sortedClosets = [...closets].sort((a, b) => {
    if (a.isDefault) return -1;
    if (b.isDefault) return 1;
    // Sort by updated_at descending (most recently edited first) for non-default closets
    return new Date(b.updated_at) - new Date(a.updated_at);
  });

  // Non-default closets that can be deleted
  const deletableClosets = sortedClosets.filter(c => !c.isDefault);
  const allSelected = deletableClosets.length > 0 && selectedClosets.length === deletableClosets.length;

  // Notify parent of state changes for header controls
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        isBulkMode,
        selectedCount: selectedClosets.length,
        closetCount: closets.length,
        allSelected,
        onNewCloset: () => setCreateClosetModalOpen(true),
        onBulkEdit: () => setIsBulkMode(true),
        onSelectAll: handleSelectAll,
        onDelete: () => setDeleteConfirmOpen(true),
        onDone: handleExitBulkMode
      });
    }
  }, [isBulkMode, selectedClosets.length, closets.length, allSelected]);

  const handleCreateCloset = async () => {
    if (!newClosetName.trim()) return;
    try {
      await createCloset({ name: newClosetName.trim(), isPublic: newClosetIsPublic }).unwrap();
      reload();
      setNewClosetName('');
      setNewClosetIsPublic(false);
      setCreateClosetModalOpen(false);
    } catch (error) {
      console.error('Failed to create closet:', error);
    }
  };

  const handleClosetSelect = (closetId) => {
    setSelectedClosets(prev =>
      prev.includes(closetId)
        ? prev.filter(id => id !== closetId)
        : [...prev, closetId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClosets.length === deletableClosets.length) {
      reload();
      setSelectedClosets([]);
    } else {
      setSelectedClosets(deletableClosets.map(c => c.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedClosets.map(id => deleteCloset(id).unwrap()));
      setSelectedClosets([]);
      setIsBulkMode(false);
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Failed to delete closets:', error);
    }
  };

  const handleExitBulkMode = () => {
    setIsBulkMode(false);
    setSelectedClosets([]);
  };

  const handleEditCloset = (closet) => {
    setEditingCloset(closet);
    setEditClosetName(closet.name);
    setEditClosetIsPublic(!!closet.isSharedForTryOn);
  };

  const handleUpdateClosetName = async () => {
    if (!editClosetName.trim() || !editingCloset) return;
    try {
      await updateCloset({ id: editingCloset.id, name: editClosetName.trim(), isPublic: editClosetIsPublic }).unwrap();
      reload();
      setEditingCloset(null);
      setEditClosetName('');
      setEditClosetIsPublic(false);
    } catch (error) {
      console.error('Failed to update closet:', error);
    }
  };

  const isHorizontal = panelPosition === 'bottom';

  return (
    <div className={`closet-panel-view ${isHorizontal ? 'horizontal-layout' : ''}`}>
      {/* Header with + New Closet and Bulk Edit - only show in right panel (vertical layout) */}
      {!isHorizontal && (
        <div className="closet-bulk-header">
          {isBulkMode ? (
            <>
              <Button
                basic
                color="teal"
                size="tiny"
                onClick={handleSelectAll}
              >
                {allSelected ? 'Unselect' : 'Select All'}
              </Button>
              <Button
                color="red"
                size="tiny"
                disabled={selectedClosets.length === 0}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete ({selectedClosets.length})
              </Button>
              <Button
                color="teal"
                size="tiny"
                onClick={handleExitBulkMode}
              >
                Done
              </Button>
            </>
          ) : (
            <>
              <Button
                basic
                color="teal"
                size="tiny"
                onClick={() => setCreateClosetModalOpen(true)}
              >
                + New Closet
              </Button>
              {closets.length > 1 && (
                <span
                  onClick={() => setIsBulkMode(true)}
                  style={{
                    color: '#00b5ad',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    padding: '2px 4px',
                    userSelect: 'none'
                  }}
                >
                  Select
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Closets Grid */}
      <div className="closets-grid">
        {closetsLoading ? (
          <div className="loading-state">
            <Loader active inline size="small" />
          </div>
        ) : (
          sortedClosets.map(closet => (
            <ClosetCard
              key={closet.id}
              closet={closet}
              onClick={() => isBulkMode && !closet.isDefault
                ? handleClosetSelect(closet.id)
                : onClosetSelect?.(closet.id, closet.name, closet.isDefault)
              }
              onEdit={handleEditCloset}
              isBulkMode={isBulkMode}
              isSelected={selectedClosets.includes(closet.id)}
              isDefault={closet.isDefault}
              isHorizontal={isHorizontal}
            />
          ))
        )}
      <InfiniteScrollSentinel hasMore={hasMore} loading={isFetching} error={isError}
          onLoadMore={loadMore} onRetry={refetch} count={closets.length} />
      </div>

      {/* Create Closet Modal */}
      <SusuModal
        open={createClosetModalOpen}
        onClose={() => setCreateClosetModalOpen(false)}
        size="mini"
      >
        <SusuModal.Header>Create New Closet</SusuModal.Header>
        <SusuModal.Content>
          <Dimmer active={creatingCloset} inverted>
            <Loader size="medium">Creating...</Loader>
          </Dimmer>
          <Input
            fluid
            placeholder="Closet name"
            value={newClosetName}
            onChange={(e) => setNewClosetName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateCloset()}
          />
          <div style={{ marginTop: '1rem' }}>
            <SusuToggle
              checked={newClosetIsPublic}
              onChange={setNewClosetIsPublic}
              label="Share to Community"
            />
          </div>
        </SusuModal.Content>
        <SusuModal.Actions>
          <Button onClick={() => setCreateClosetModalOpen(false)} disabled={creatingCloset}>Cancel</Button>
          <Button
            color="teal"
            onClick={handleCreateCloset}
            disabled={!newClosetName.trim() || creatingCloset}
          >
            Create
          </Button>
        </SusuModal.Actions>
      </SusuModal>

      {/* Delete Confirmation Modal */}
      <SusuModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        size="mini"
      >
        <SusuModal.Header>Delete Closets</SusuModal.Header>
        <SusuModal.Content>
          <Dimmer active={deletingCloset} inverted>
            <Loader size="medium">Deleting...</Loader>
          </Dimmer>
          <p>Are you sure you want to delete {selectedClosets.length} closet{selectedClosets.length > 1 ? 's' : ''}?</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            Clothing items will be moved back to your main closet.
          </p>
        </SusuModal.Content>
        <SusuModal.Actions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deletingCloset}>Cancel</Button>
          <Button
            color="red"
            onClick={handleBulkDelete}
            disabled={deletingCloset}
          >
            Delete
          </Button>
        </SusuModal.Actions>
      </SusuModal>

      {/* Rename Closet Modal */}
      <SusuModal
        open={!!editingCloset}
        onClose={() => setEditingCloset(null)}
        size="mini"
      >
        <SusuModal.Header>Rename Closet</SusuModal.Header>
        <SusuModal.Content>
          <Dimmer active={updatingCloset} inverted>
            <Loader size="medium">Updating...</Loader>
          </Dimmer>
          <Input
            fluid
            placeholder="Closet name"
            value={editClosetName}
            onChange={(e) => setEditClosetName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleUpdateClosetName()}
          />
          <div style={{ marginTop: '1rem' }}>
            <SusuToggle
              checked={editClosetIsPublic}
              onChange={setEditClosetIsPublic}
              label="Share to Community"
            />
          </div>
        </SusuModal.Content>
        <SusuModal.Actions>
          <Button onClick={() => setEditingCloset(null)} disabled={updatingCloset}>Cancel</Button>
          <Button
            color="teal"
            onClick={handleUpdateClosetName}
            disabled={!editClosetName.trim() || updatingCloset}
          >
            Save
          </Button>
        </SusuModal.Actions>
      </SusuModal>
    </div>
  );
};

export default ClosetPanelView;
